import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { EyeIcon, ChatBubbleLeftIcon, PencilSquareIcon, UserPlusIcon, CheckCircleIcon, XCircleIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { contentApi, ContentType, ActivityEntry } from '@/services/contentApi';
import { useAuth } from '@/hooks/useAuth';

interface ContentActivityPanelProps {
  contentType: ContentType;
  contentId: string;
}

function actorName(actor: ActivityEntry['actorId']): string {
  return typeof actor === 'string' ? actor : actor.name;
}

const ACTION_ICON: Record<ActivityEntry['action'], React.ElementType> = {
  viewed: EyeIcon,
  updated: PencilSquareIcon,
  assigned: UserPlusIcon,
  reassigned: UserPlusIcon,
  commented: ChatBubbleLeftIcon,
  created: PencilSquareIcon,
  linked: PencilSquareIcon,
  review_requested: ClipboardDocumentCheckIcon,
  review_approved: CheckCircleIcon,
  review_rejected: XCircleIcon,
};

const ACTION_LABEL: Record<ActivityEntry['action'], string> = {
  viewed: 'viewed this',
  updated: 'updated this',
  assigned: 'assigned this',
  reassigned: 'reassigned this',
  commented: 'commented',
  created: 'created this',
  linked: 'linked related content',
  review_requested: 'requested a review',
  review_approved: 'approved this',
  review_rejected: 'rejected this',
};

/**
 * Drop-in "who viewed/opened/updated this, plus comments" panel, shared
 * across every content type's detail page. Fires the one-time
 * POST .../view on mount (never from a polling query) so view tracking
 * doesn't get flooded by job-status polling elsewhere on the same page.
 */
const ContentActivityPanel: React.FC<ContentActivityPanelProps> = ({ contentType, contentId }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [commentBody, setCommentBody] = useState('');

  useEffect(() => {
    contentApi.recordView(contentType, contentId).catch(() => {
      // Best-effort — a failed view-tracking call shouldn't disrupt the page.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType, contentId]);

  const { data: activity } = useQuery({
    queryKey: ['content-activity', contentType, contentId],
    queryFn: () => contentApi.getActivity(contentType, contentId),
  });

  const { data: comments } = useQuery({
    queryKey: ['content-comments', contentType, contentId],
    queryFn: () => contentApi.getComments(contentType, contentId),
  });

  const addCommentMutation = useMutation({
    mutationFn: () => contentApi.addComment(contentType, contentId, commentBody.trim()),
    onSuccess: () => {
      setCommentBody('');
      queryClient.invalidateQueries({ queryKey: ['content-comments', contentType, contentId] });
      queryClient.invalidateQueries({ queryKey: ['content-activity', contentType, contentId] });
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to add comment'),
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => contentApi.deleteComment(contentType, contentId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-comments', contentType, contentId] });
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to delete comment'),
  });

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-4">
      {/* Viewers */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Viewed by</h3>
        {activity?.viewers?.length ? (
          <div className="flex flex-wrap gap-2">
            {activity.viewers.map((viewer) => (
              <span
                key={viewer.userId}
                title={`${viewer.name || viewer.email || viewer.userId} — last viewed ${formatDistanceToNow(new Date(viewer.lastViewedAt), { addSuffix: true })}`}
                className="inline-flex items-center gap-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full px-2.5 py-1"
              >
                {viewer.name || viewer.email}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400">No one else has viewed this yet.</p>
        )}
      </div>

      {/* Activity timeline */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Activity</h3>
        <ul className="space-y-1.5 max-h-48 overflow-y-auto">
          {(activity?.timeline ?? []).map((entry) => {
            const Icon = ACTION_ICON[entry.action];
            return (
              <li key={entry._id} className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="font-medium text-gray-700 dark:text-gray-300">{actorName(entry.actorId)}</span>
                {ACTION_LABEL[entry.action]}
                <span className="text-gray-400">· {formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true })}</span>
              </li>
            );
          })}
          {!activity?.timeline?.length && <li className="text-xs text-gray-400">No activity yet.</li>}
        </ul>
      </div>

      {/* Comments */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Comments</h3>
        <ul className="space-y-3 mb-3">
          {(comments ?? []).map((comment) => {
            const author = typeof comment.authorId === 'string' ? null : comment.authorId;
            const isOwn = author?._id === user?.id;
            return (
              <li key={comment._id} className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm text-gray-900 dark:text-white">
                    <span className="font-medium">{author?.name || 'Someone'}</span>{' '}
                    <span className="text-xs text-gray-400">{formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}</span>
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{comment.body}</p>
                </div>
                {isOwn && (
                  <button
                    onClick={() => deleteCommentMutation.mutate(comment._id)}
                    className="text-xs text-gray-400 hover:text-red-500 flex-shrink-0"
                  >
                    Delete
                  </button>
                )}
              </li>
            );
          })}
          {!comments?.length && <li className="text-xs text-gray-400">No comments yet.</li>}
        </ul>

        <div className="flex gap-2">
          <input
            value={commentBody}
            onChange={(e) => setCommentBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && commentBody.trim()) addCommentMutation.mutate(); }}
            placeholder="Add a comment…"
            className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-2"
          />
          <button
            onClick={() => addCommentMutation.mutate()}
            disabled={!commentBody.trim() || addCommentMutation.isLoading}
            className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
          >
            Post
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentActivityPanel;
